<template>
  <!-- Design: ResultsPagination — per page, first/previous, page select, next/last -->
  <div class="ts-results-toolbar d-flex flex-wrap align-items-center justify-content-between gap-3">
    <div class="d-flex align-items-center gap-2">
      <label class="small" :for="`${idPrefix}PerPage`">{{ text.perPage }}</label>
      <select
        :id="`${idPrefix}PerPage`"
        class="form-select ts-control-sm ts-per-page"
        :value="size"
        @change="$emit('update:perPage', Number($event.target.value))"
      >
        <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
      </select>
    </div>

    <nav :aria-label="text.pageNavigation">
      <ul class="pagination pagination-sm ts-simple-pagination gap-2 mb-0">
        <li class="page-item" :class="{ disabled: isFirst }">
          <button class="page-link" type="button" :aria-label="text.firstPage" :disabled="isFirst" @click="goTo(1)">{{ formatCount(1) }}</button>
        </li>
        <li class="page-item" :class="{ disabled: isFirst }">
          <button class="page-link" type="button" :aria-label="text.previousPage" :disabled="isFirst" @click="goTo(current - 1)">«</button>
        </li>

        <li class="page-item dropdown ts-page-select" @keydown.esc="closeMenu(true)">
          <button
            ref="toggle"
            class="page-link ts-page-select-toggle d-inline-flex align-items-center gap-2"
            :class="{ show: open }"
            type="button"
            :aria-expanded="String(open)"
            :aria-label="`${text.page} ${formatCount(current)}`"
            @click="open ? closeMenu() : openMenu()"
          >
            <span class="ts-page-select-label">{{ text.page }} <strong>{{ formatCount(current) }}</strong></span>
            <i class="bi bi-chevron-down ts-page-select-chevron-down" aria-hidden="true"></i>
            <i class="bi bi-chevron-up ts-page-select-chevron-up" aria-hidden="true"></i>
          </button>
          <div v-if="open" class="dropdown-menu dropdown-menu-end ts-page-select-menu shadow show" :style="menuStyle">
            <form class="ts-page-select-search" @submit.prevent="submitSearch">
              <input
                ref="search"
                v-model="query"
                class="form-control form-control-sm"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                :aria-label="text.pageSearch.replace('…', '').trim()"
                :placeholder="text.pageSearch"
              />
            </form>
            <ul ref="list" class="list-unstyled ts-page-select-list mb-0">
              <template v-for="(item, index) in visiblePages">
                <li v-if="item === 'ellipsis'" :key="`ellipsis-${index}`" class="ts-page-select-separator" aria-hidden="true">…</li>
                <li v-else :key="item">
                  <button
                    class="dropdown-item search-option"
                    :class="{ active: item === current }"
                    type="button"
                    :aria-current="item === current ? 'page' : null"
                    @click="choose(item)"
                  >
                    <span class="search-option-label">{{ text.page }} {{ formatCount(item) }}</span>
                    <span class="search-option-check" aria-hidden="true"><i class="bi bi-check2"></i></span>
                  </button>
                </li>
              </template>
              <li v-if="query.trim() && !visiblePages.length" class="ts-page-select-empty">{{ text.pageNotFound }}</li>
            </ul>
            <p class="ts-page-select-hint mb-0">{{ text.pageOf }} {{ formatCount(lastPage) }} {{ text.pages }}</p>
          </div>
        </li>

        <li class="page-item" :class="{ disabled: isLast }">
          <button class="page-link" type="button" :aria-label="text.nextPage" :disabled="isLast" @click="goTo(current + 1)">»</button>
        </li>
        <li class="page-item" :class="{ disabled: isLast }">
          <button class="page-link" type="button" :aria-label="text.lastPage" :disabled="isLast" @click="goTo(lastPage)">{{ formatCount(lastPage) }}</button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { formatCount } from '@/helpers/formatCount';

type PageEntry = number | 'ellipsis';

// Pages around the current one (±5), the first and the last; gaps as "…"
const pageWindow = (page: number, lastPage: number): PageEntry[] => {
  const items: PageEntry[] = [1];
  const from = Math.max(2, page - 5);
  const to = Math.min(lastPage - 1, page + 5);
  if (from > 2) items.push('ellipsis');
  for (let item = from; item <= to; item += 1) items.push(item);
  if (to < lastPage - 1) items.push('ellipsis');
  if (lastPage > 1) items.push(lastPage);
  return items;
};

// Only shows the paging and reports the choice (.sync page / perPage); loading is up to the page
@Component
export default class TsResultsPagination extends Vue {
  @Prop({ required: true }) readonly idPrefix!: string;

  @Prop({ default: 1 }) readonly page!: number;

  @Prop({ default: 20 }) readonly perPage!: number;

  @Prop({ default: 0 }) readonly total!: number;

  pageSizes = [20, 50, 100];

  open = false;

  query = '';

  // Under the toggle, or above it near the page end (no Popper here)
  menuStyle: Record<string, string> = {};

  formatCount = formatCount;

  get text() {
    return {
      perPage: AppModule.translation[16557],
      page: AppModule.translation[3074],
      pageOf: AppModule.translation[361],
      pages: AppModule.translation[16558],
      pageSearch: AppModule.translation[16176],
      pageNotFound: AppModule.translation[16206],
      pageNavigation: AppModule.translation[16559],
      firstPage: AppModule.translation[16560],
      previousPage: AppModule.translation[3385],
      nextPage: AppModule.translation[3383],
      lastPage: AppModule.translation[16561],
    };
  }

  // The store keeps them as strings once restored from a link
  get current(): number {
    return Number(this.page) || 1;
  }

  get size(): number {
    return Number(this.perPage) || 20;
  }

  get lastPage() {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  get isFirst() {
    return this.current <= 1;
  }

  get isLast() {
    return this.current >= this.lastPage;
  }

  // A typed number shows only that page (when it exists)
  get visiblePages(): PageEntry[] {
    const typed = this.query.trim();
    if (!typed) return pageWindow(this.current, this.lastPage);
    if (!/^\d+$/.test(typed)) return [];
    const page = Number(typed);
    return page >= 1 && page <= this.lastPage ? [page] : [];
  }

  goTo(page: number) {
    const next = Math.min(this.lastPage, Math.max(1, Math.trunc(page)));
    if (next !== this.current) this.$emit('update:page', next);
  }

  choose(page: number) {
    this.closeMenu(true);
    this.goTo(page);
  }

  submitSearch() {
    const [page] = this.visiblePages;
    if (typeof page === 'number') this.choose(page);
  }

  openMenu() {
    this.query = '';
    this.place();
    this.open = true;
    document.addEventListener('click', this.closeOutside);
    this.$nextTick(() => {
      (this.$refs.search as HTMLInputElement | undefined)?.focus();
      // The current page in the middle of the list
      const list = this.$refs.list as HTMLElement | undefined;
      const current = list?.querySelector('.search-option.active') as HTMLElement | null;
      if (list && current) {
        list.scrollTop = Math.max(0, current.offsetTop - (list.clientHeight - current.offsetHeight) / 2);
      }
    });
  }

  closeMenu(focusToggle = false) {
    this.open = false;
    document.removeEventListener('click', this.closeOutside);
    if (focusToggle) (this.$refs.toggle as HTMLElement | undefined)?.focus();
  }

  place() {
    const gap = 16;
    const menuHeight = 340; // search, 240px list and the hint
    const toggle = (this.$refs.toggle as HTMLElement).getBoundingClientRect();
    const below = window.innerHeight - toggle.bottom - gap;
    const openUp = below < menuHeight && toggle.top - gap > below;
    this.menuStyle = openUp
      ? {
        top: 'auto', bottom: '100%', right: '0', left: 'auto', marginBottom: '2px',
      }
      : {
        top: '100%', bottom: 'auto', right: '0', left: 'auto', marginTop: '2px',
      };
  }

  closeOutside(event: MouseEvent) {
    if (!this.$el.querySelector('.ts-page-select')?.contains(event.target as Node)) this.closeMenu();
  }

  beforeDestroy() {
    document.removeEventListener('click', this.closeOutside);
  }
}
</script>
